/**
 * The inspector's cover plate: the photograph, what the plan is, and three figures.
 *
 * ── Over a TEMPLATE nothing here waits; over a CLIENT'S PLAN the sub-line and two figures do ──
 * A template's plate is built entirely from the list row the trainer pointed at, which arrived with
 * the screen — the cover, the category, the name, the span, the rhythm, the freshness, the exercise
 * count and the follower count are all on it. So a swap paints a complete plate on its first frame,
 * and `/full` fills in only what is below the plate.
 *
 * A client's plan has no such row. Its list row is an ASSIGNMENT, and `PlanAssignmentSummary`
 * carries no phases, no cycle count and no `updatedAt` — those three facts live on the plan itself
 * and arrive with `/full` as `InspectorView.overview`. What the assignment DOES carry is the cover,
 * the category, the plan's name and the progress, so the plate's photograph, chip, title and third
 * figure are complete on the first frame either way.
 *
 * **The sub-line therefore reserves its line whether or not it has words yet.** `.foot` is anchored
 * to the plate's bottom edge, so a line that appears when a read lands pushes the TITLE up, which is
 * the jump this repository treats as a defect. The element is always rendered and the stylesheet
 * holds one line's height; see `InspectorHero.module.css`.
 *
 * ── A dash under a figure means the plan cannot state it, and the sentence beside it says which ─
 * The specimen's own rule for the follower count: `P.cl.length ? String(P.cl.length) : '—'`. That is
 * a different dash from the page header's, which stands for a figure nobody knows YET, and one glyph
 * carrying two meanings on one screen is exactly what "never encode meaning in a mark alone" is
 * about. So the zero case carries the library's own sentence for it, hidden, and a reader hears
 * "nobody is following this programme yet" rather than a dash.
 *
 * ── `Duplikuj` is not here, and that is owner decision D2 ────────────────────────────────────
 * The canon draws a ghost button beside `Edytuj`. There is no duplicate-plan endpoint and no plan
 * to add one this round; a control that cannot act is worse than an absent one. Dropping it moves
 * nothing — `Edytuj` is pinned to the plate's right edge by a flexible spacer, exactly as the
 * specimen pins it.
 *
 * ── The plate's ink is white in BOTH themes, and its tokens say so ───────────────────────────
 * A photograph under a dark veil is a photograph in both themes. `var(--text)` here would be black
 * on Flame and invisible; `--text-on-photo` and the two `--…-on-cover` steps are theme-independent
 * for the same reason `--photo-scrim` is.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { relativeTimeFormat } from '~/shared/lib/intlCache';
import photoBox from '~/shared/ui/photoBox.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { InspectorSubject } from '../model/inspectorSubject';
import { daysInRepeatUnit, type InspectorView, type PlanOverview } from '../model/inspectorView';
import { freshnessOf } from '../model/planFreshness';
import type { PlanActivity, PlanRhythm, PlanSpan } from '../model/planLibraryRow';
import { spanLabel } from './spanLabel';
import styles from './InspectorHero.module.css';

interface InspectorHeroProps {
  readonly subject: InspectorSubject;
  /** The plan itself. `null` while `/full` is open, which is what the sub-line's reserve is for. */
  readonly plan: InspectorView | null;
}

/** One figure and the word under it. `figure === null` is the canon's em dash. */
interface Kpi {
  readonly key: string;
  /** Pre-formatted, because the third one is a percent and the other two are counts. */
  readonly figure: string | null;
  readonly label: string;
  /** Said instead of the mark when the figure alone would not carry the fact. */
  readonly spoken?: string;
}

/** `8 tygodni · 4 dni / tydz. · zmieniony 2 dni temu`, with each part dropped when it is not known. */
function subline(
  span: PlanSpan | null,
  rhythm: PlanRhythm | null,
  activity: PlanActivity | null,
  when: string | null,
  t: TFunction,
): string {
  return [
    spanLabel(span, t),
    rhythm === null
      ? null
      : rhythm.kind === 'week'
        ? t('web:planLibrary.rhythm.week', { count: rhythm.daysPerWeek })
        : t('web:planLibrary.rhythm.cycle', { count: rhythm.cycles }),
    activity === null || when === null
      ? null
      : t(`web:planLibrary.${activity.kind === 'edited' ? 'changed' : 'created'}`, { when }),
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

/**
 * The first two figures: how many exercises the plan holds, and how many days its repeat unit has.
 *
 * One function for both subjects, because it is one question asked of whichever source can answer
 * it — a template's row, or the plan itself. The DAY figure's label is the mode's: a weekly plan
 * counts days in a week, a self-paced one counts days in its cycle, and the two are different
 * sentences rather than one with a variable noun.
 */
function shapeKpis(
  exerciseCount: number | null,
  days: number | null,
  selfPaced: boolean,
  t: TFunction,
): readonly Kpi[] {
  return [
    {
      key: 'exercises',
      figure: exerciseCount === null ? null : String(exerciseCount),
      label: t('web:planLibrary.inspector.kpi.exercises', { count: exerciseCount ?? 0 }),
    },
    {
      key: 'days',
      figure: days === null ? null : String(days),
      label: t(
        selfPaced
          ? 'web:planLibrary.inspector.kpi.daysInCycle'
          : 'web:planLibrary.inspector.kpi.daysPerWeek',
        { count: days ?? 0 },
      ),
    },
  ];
}

export function InspectorHero({ subject, plan }: InspectorHeroProps) {
  const { t, i18n } = useTranslation();

  const overview: PlanOverview | null = plan?.overview ?? null;
  // A template states its own facts; a client's plan borrows the plan's, which is why one of the two
  // waits and the other does not. See the header.
  const activity = subject.kind === 'template' ? subject.row.activity : (overview?.activity ?? null);
  const freshness = activity === null ? null : freshnessOf(activity.at, new Date());
  const when =
    freshness === null
      ? null
      : relativeTimeFormat(i18n.language, { numeric: 'auto' }).format(
          -freshness.value,
          freshness.unit,
        );

  const selfPaced = subject.row.schedulingMarker === 'selfPaced';
  // WHICH source answers the day figure is `model/`'s decision, not this file's, and it is the same
  // decision the sub-line beside it makes — a hero that chose here printed one figure in its
  // sentence and a different one under it. A client's plan has no list row to quote, so it passes
  // no rhythm and the plan's own tree answers both.
  const shape =
    subject.kind === 'template'
      ? shapeKpis(
          subject.row.exerciseCount,
          daysInRepeatUnit(subject.row.rhythm, overview),
          selfPaced,
          t,
        )
      : shapeKpis(
          overview?.exerciseCount ?? null,
          daysInRepeatUnit(null, overview),
          selfPaced,
          t,
        );

  const paused = subject.kind === 'clientPlan' && subject.row.status === 'paused';
  const progressCount =
    subject.kind === 'template'
      ? ''
      : t('web:planLibrary.inspector.kpiProgress', {
          count: subject.row.workoutsTotal,
          done: subject.row.workoutsDone,
        });

  const kpis: readonly Kpi[] =
    subject.kind === 'template'
      ? [
          ...shape,
          {
            key: 'assigned',
            // The specimen's own rule: a zero prints as the dash rather than as `0`.
            figure:
              subject.row.assignedClientCount === 0
                ? null
                : String(subject.row.assignedClientCount),
            label: t('web:planLibrary.stats.assigned'),
            spoken:
              subject.row.assignedClientCount === 0
                ? t('web:planLibrary.noClients')
                : t('web:planLibrary.row.clients', { count: subject.row.assignedClientCount }),
          },
        ]
      : [
          ...shape,
          {
            key: 'progress',
            // The figure is the PERCENT and the word under it is the count it came from, which is
            // owner decision D7 read onto the plate: `40%` over `12 Z 30 TRENINGÓW`.
            figure: t('web:planLibrary.clientPlans.percent', {
              percent: subject.row.progressPct,
            }),
            // A PAUSED plan says so here, and the pane said it nowhere else: an inspector over a
            // paused assignment was byte-identical to one over a live assignment on the same
            // template, and triage is the trainer's whole task on this screen. The row's own word,
            // in front of the count rather than instead of it — the figures are still true.
            label: paused
              ? `${t('web:planLibrary.clientPlans.paused')} · ${progressCount}`
              : progressCount,
          },
        ];

  const title = subject.kind === 'template' ? subject.row.name : subject.row.planName;
  const sub =
    subject.kind === 'template'
      ? subline(subject.row.span, subject.row.rhythm, subject.row.activity, when, t)
      : subline(overview?.span ?? null, overview?.rhythm ?? null, activity, when, t);

  return (
    <div className={styles.hero}>
      <img
        className={`${String(photoBox.photo)} ${String(styles.cover)}`}
        src={subject.row.heroUrl}
        alt=""
        decoding="async"
      />
      <span className={styles.veil} aria-hidden />

      <span className={styles.top}>
        {subject.row.category === null ? null : (
          <span className={styles.chip}>
            <i className={styles.chipDot} aria-hidden />
            {subject.row.category}
          </span>
        )}
        <span className={styles.spacer} aria-hidden />
        <Link
          className={styles.edit}
          to="/plans/$planId"
          // A client's plan opens THEIR fork, not the template it came from.
          params={{ planId: String(subject.planId) }}
        >
          {t('web:planLibrary.inspector.edit')}
        </Link>
      </span>

      <span className={styles.foot}>
        <h2 className={styles.title}>{title}</h2>
        {/* Always rendered, empty or not: `.foot` grows upward from the plate's bottom edge, so a
          * sub-line that appeared when `/full` landed would push the title up by its own height. */}
        <span className={styles.sub}>{sub}</span>
        <span className={styles.kpis}>
          {kpis.map((kpi) => (
            <span key={kpi.key} className={styles.kpi}>
              <span className={styles.figure} aria-hidden={kpi.spoken !== undefined}>
                {kpi.figure ?? t('web:planLibrary.states.figureUnknown')}
              </span>
              <span className={styles.label} aria-hidden={kpi.spoken !== undefined}>
                {kpi.label}
              </span>
              {kpi.spoken === undefined ? null : (
                <span className={hidden.srOnly}>{kpi.spoken}</span>
              )}
            </span>
          ))}
        </span>
      </span>
    </div>
  );
}
